import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Car, Truck, Menu, X, Phone, MapPin, Star, Shield, CheckCircle, 
  Calendar, Clock, ChevronDown, ChevronUp, ChevronRight, Sparkles, Droplets,
  Award, Users, ArrowRight, Mail, MessageCircle, Search, Upload, Image, Camera,
  Gift, Copy, Share2, AlertCircle, Edit3, DollarSign, Trash2, Plus, Ban, Check, Loader2, Cake
} from "lucide-react";

// Import refactored pages
import { 
  HomePage, 
  PackagesPage as PackagesPageNew, 
  ServiceAreasPage as ServiceAreasPageNew, 
  ContactPage as ContactPageNew, 
  TermsPage as TermsPageNew,
  BookingSuccessPage,
  HowItWorksPage,
  PaymentSuccessPage,
  TradePage,
  AccountPage,
  AdminPage
} from "./pages";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { Calendar as CalendarComponent } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import { format } from "date-fns";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";

// Import constants from centralized location
import { 
  BACKEND_URL, API, GOOGLE_MAPS_API_KEY, 
  SERVICE_AREAS, FEATURES, TIME_SLOTS, ADMIN_TIMES,
  getStatusColor, formatStatus, POLICY_SECTIONS, SUBURBS_DATA
} from "./lib/constants";

// Import contexts from centralized location
import { 
  AuthProvider, useAuth, 
  BookingProvider, useBooking, PACKAGES, ADDONS, VEHICLE_TYPES 
} from "./context";


// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setShowBookingModal } = useBooking();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-strong py-3" : "py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="logo">
          <img 
            src="/logo.png" 
            alt="RainXpress Logo" 
            className="h-12 w-12 rounded-full object-cover"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          <button onClick={() => handleNavigation("/")} className={`transition ${location.pathname === "/" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Home</button>
          <button onClick={() => handleNavigation("/packages")} className={`transition ${location.pathname === "/packages" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Packages</button>
          <button onClick={() => handleNavigation("/how-it-works")} className={`transition ${location.pathname === "/how-it-works" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>How It Works</button>
          <button onClick={() => handleNavigation("/service-areas")} className={`transition ${location.pathname === "/service-areas" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Service Areas</button>
          <button onClick={() => handleNavigation("/contact")} className={`transition ${location.pathname === "/contact" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>Contact</button>
          <button onClick={() => handleNavigation("/terms")} className={`transition ${location.pathname === "/terms" ? "text-cyan-400" : "text-slate-300 hover:text-cyan-400"}`}>T&Cs</button>
        </nav>

        <div className="flex items-center gap-3">
          <a 
            href="tel:0478054648" 
            className="hidden lg:flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition"
            data-testid="phone-link"
          >
            <Phone className="w-4 h-4" />
            <span className="text-sm font-medium">0478 054 648</span>
          </a>
          
          {/* Auth Buttons - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <div className="relative group">
                <button 
                  onClick={() => handleNavigation("/account")}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 transition text-sm"
                  data-testid="account-btn"
                >
                  <Users className="w-4 h-4 text-cyan-400" />
                  <span className="text-slate-200">{user.name?.split(' ')[0]}</span>
                </button>
              </div>
            ) : (
              <button 
                onClick={() => handleNavigation("/account")}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 transition text-sm text-slate-200"
                data-testid="login-btn"
              >
                <Users className="w-4 h-4" />
                Login
              </button>
            )}
          </div>
          
          <Button 
            onClick={() => setShowBookingModal(true)}
            className="btn-primary-glow text-slate-900 font-semibold px-6 py-2 rounded-full"
            data-testid="header-book-now-btn"
          >
            Book Now
          </Button>
          <button 
            className="md:hidden text-white" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-strong mt-2 mx-4 rounded-xl overflow-hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col p-4 gap-4">
              <button onClick={() => handleNavigation("/")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Home</button>
              <button onClick={() => handleNavigation("/packages")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Packages</button>
              <button onClick={() => handleNavigation("/how-it-works")} className="text-left py-2 text-slate-300 hover:text-cyan-400">How It Works</button>
              <button onClick={() => handleNavigation("/service-areas")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Service Areas</button>
              <button onClick={() => handleNavigation("/contact")} className="text-left py-2 text-slate-300 hover:text-cyan-400">Contact</button>
              <button onClick={() => handleNavigation("/terms")} className="text-left py-2 text-slate-300 hover:text-cyan-400">T&Cs</button>
              
              {/* Auth - Mobile */}
              <div className="border-t border-slate-700 pt-4 mt-2">
                {user ? (
                  <>
                    <button 
                      onClick={() => handleNavigation("/account")} 
                      className="flex items-center gap-2 py-2 text-cyan-400"
                      data-testid="mobile-account-btn"
                    >
                      <Users className="w-4 h-4" />
                      My Account
                    </button>
                    <button 
                      onClick={handleLogout} 
                      className="flex items-center gap-2 py-2 text-slate-400 hover:text-red-400"
                      data-testid="mobile-logout-btn"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => handleNavigation("/account")} 
                    className="flex items-center gap-2 py-2 text-cyan-400"
                    data-testid="mobile-login-btn"
                  >
                    <Users className="w-4 h-4" />
                    Login / Sign Up
                  </button>
                )}
              </div>
              
              <a href="tel:0478054648" className="flex items-center gap-2 py-2 text-cyan-400">
                <Phone className="w-4 h-4" />
                0478 054 648
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { setShowBookingModal } = useBooking();
  const navigate = useNavigate();

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 stars-bg" />
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1613039704499-1975f634d75f?w=1920&q=80')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/50 to-slate-950" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-lg md:text-xl text-slate-400 mb-4" data-testid="hero-subtitle">
            Melbourne Mobile Detailing
          </p>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight" data-testid="hero-headline">
            <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-white bg-clip-text text-transparent">RainXpress</span>
          </h1>
          
          <p className="text-base md:text-lg text-slate-400 mb-10 max-w-2xl mx-auto" data-testid="hero-subheadline">
            We come to you.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Button 
              onClick={() => setShowBookingModal(true)}
              className="btn-primary-glow text-slate-900 font-bold px-8 py-4 rounded-full text-lg flex items-center gap-2 justify-center"
              data-testid="hero-book-now-btn"
            >
              <Calendar className="w-5 h-5" />
              Book Now
            </Button>
            <Button 
              onClick={() => navigate('/services')}
              className="btn-secondary font-semibold px-8 py-4 rounded-full text-lg flex items-center gap-2 justify-center"
              data-testid="hero-view-services-btn"
            >
              View Services
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>

          {/* Unsure message */}
          <p className="text-slate-400 text-sm mb-8 max-w-xl mx-auto">
            Unsure of what to book or need an accurate price quotation with travel fee?{' '}
            <a href="tel:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">Call</a> or{' '}
            <a href="sms:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">text now</a> and we respond same day.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-8" data-testid="trust-badges">
            <div className="trust-badge">
              <Award className="w-5 h-5" />
              <span>Professional Equipment</span>
            </div>
            <div className="trust-badge">
              <Users className="w-5 h-5" />
              <span>Satisfied Customers</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-8 h-8 text-cyan-400/50" />
      </motion.div>
    </section>
  );
};

// Emotional Appeal Section
const EmotionalSection = () => {
  return (
    <section className="section-spacing bg-slate-950" data-testid="emotional-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-lg md:text-xl text-slate-300 space-y-4 mb-10">
            <p className="text-slate-400">Over time, life gets busy.</p>
            <p className="text-slate-400">Dust, crumbs, stains, and road grime slowly build up.</p>
            <p className="text-cyan-400 font-medium text-xl md:text-2xl">We refine and maintain without you lifting a finger.</p>
          </div>

          {/* Brand Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {[
              { icon: Clock, title: "Protect Your Time", desc: "We come to you, wherever you are" },
              { icon: Shield, title: "Protect Your Investment", desc: "Professional care extends vehicle life" },
              { icon: Sparkles, title: "Protect Your Pride", desc: "Drive with confidence every day" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <item.icon className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Vehicle Type Selector
const VehicleSelector = () => {
  const { vehicleType, setVehicleType } = useBooking();

  const vehicles = [
    { id: "small", label: "Small Car", icon: Car },
    { id: "medium", label: "SUV / Med", icon: Car },
    { id: "large", label: "Large / Van", icon: Truck },
    { id: "xl", label: "XL / Truck", icon: Truck }
  ];

  return (
    <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-8" data-testid="vehicle-selector">
      {vehicles.map((v) => (
        <button
          key={v.id}
          onClick={() => setVehicleType(v.id)}
          className={`vehicle-selector-btn flex items-center gap-2 px-4 py-3 rounded-full font-medium transition-all ${
            vehicleType === v.id ? "active" : ""
          }`}
          data-testid={`vehicle-${v.id}-btn`}
        >
          <v.icon className="w-5 h-5" />
          <span className="hidden sm:inline">{v.label}</span>
          <span className="sm:hidden">{v.id === "medium" ? "SUV" : v.id === "large" ? "Van" : v.id === "xl" ? "XL" : "Small"}</span>
        </button>
      ))}
    </div>
  );
};

// Package Card Component
const PackageCard = ({ packageKey, pkg }) => {
  const { vehicleType, setSelectedPackage, setShowBookingModal } = useBooking();
  const [showAllInterior, setShowAllInterior] = useState(false);
  const [showAllExterior, setShowAllExterior] = useState(false);
  const price = pkg.basePrices[vehicleType] || pkg.basePrices.small;

  const handleSelect = () => {
    setSelectedPackage(packageKey);
    setShowBookingModal(true);
  };

  const interiorToShow = showAllInterior ? pkg.interior : pkg.interior.slice(0, 4);
  const exteriorToShow = showAllExterior ? pkg.exterior : pkg.exterior.slice(0, 3);
  const hiddenInterior = pkg.interior.length - 4;
  const hiddenExterior = pkg.exterior.length - 3;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative glass rounded-2xl p-6 card-hover ${pkg.popular ? "package-highlight" : ""}`}
      data-testid={`package-card-${packageKey}`}
    >
      {pkg.popular && <div className="most-popular-badge">Most Popular</div>}
      
      <h3 className="text-2xl font-bold mb-1">{pkg.name}</h3>
      {pkg.includes && <p className="text-cyan-400 text-sm mb-2">{pkg.includes}</p>}
      
      <div className="text-4xl font-black gradient-text mb-2">${price}</div>
      <p className="text-slate-400 text-sm mb-6">{pkg.tagline}</p>

      <div className="space-y-4 mb-6">
        <div>
          <h4 className="font-semibold text-cyan-400 mb-2 text-sm">Interior:</h4>
          <ul className="space-y-1">
            {interiorToShow.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {hiddenInterior > 0 && !showAllInterior && (
            <button 
              onClick={() => setShowAllInterior(true)}
              className="text-cyan-400 text-sm mt-2 hover:text-cyan-300 flex items-center gap-1"
            >
              <ChevronDown className="w-4 h-4" />
              +{hiddenInterior} more
            </button>
          )}
          {showAllInterior && hiddenInterior > 0 && (
            <button 
              onClick={() => setShowAllInterior(false)}
              className="text-slate-500 text-sm mt-2 hover:text-slate-400 flex items-center gap-1"
            >
              <ChevronUp className="w-4 h-4" />
              Show less
            </button>
          )}
        </div>
        
        <div>
          <h4 className="font-semibold text-cyan-400 mb-2 text-sm">Exterior:</h4>
          <ul className="space-y-1">
            {exteriorToShow.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {hiddenExterior > 0 && !showAllExterior && (
            <button 
              onClick={() => setShowAllExterior(true)}
              className="text-cyan-400 text-sm mt-2 hover:text-cyan-300 flex items-center gap-1"
            >
              <ChevronDown className="w-4 h-4" />
              +{hiddenExterior} more
            </button>
          )}
          {showAllExterior && hiddenExterior > 0 && (
            <button 
              onClick={() => setShowAllExterior(false)}
              className="text-slate-500 text-sm mt-2 hover:text-slate-400 flex items-center gap-1"
            >
              <ChevronUp className="w-4 h-4" />
              Show less
            </button>
          )}
        </div>
      </div>

      <Button 
        onClick={handleSelect}
        className={`w-full rounded-full py-3 font-semibold ${
          pkg.popular 
            ? "btn-primary-glow text-slate-900" 
            : "bg-slate-700 hover:bg-slate-600 text-white"
        }`}
        data-testid={`package-select-${packageKey}-btn`}
      >
        <ArrowRight className="w-4 h-4 mr-2" />
        Get Started
      </Button>

      <p className="text-xs text-slate-500 mt-4 text-center">
        *Heavy duty cleaning may include additional charges
      </p>
    </motion.div>
  );
};

// Packages Section
const PackagesSection = () => {
  return (
    <section id="packages" className="section-spacing bg-slate-900/50" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Detailing <span className="gradient-text">Packages</span>
          </h2>
          <p className="text-slate-400 mb-8">Select your vehicle size to see pricing</p>
          
          <VehicleSelector />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {Object.entries(PACKAGES).map(([key, pkg]) => (
            <PackageCard key={key} packageKey={key} pkg={pkg} />
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseUsSection = () => {
  const features = [
    { icon: MapPin, title: "We Come To You", desc: "Home, work, anywhere in Melbourne" },
    { icon: Clock, title: "Flexible Scheduling", desc: "Book a time that works for you" },
    { icon: Sparkles, title: "Premium Products", desc: "Only the best for your vehicle" },
    { icon: Users, title: "Passionate Team", desc: "We love what we do" },
    { icon: Award, title: "Satisfied Customers", desc: "Quality service guaranteed" }
  ];

  return (
    <section className="section-spacing bg-slate-950" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Why Choose <span className="gradient-text">RainXpress</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="glass rounded-xl p-4 md:p-6 text-center card-hover"
            >
              <feature.icon className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
              <h3 className="font-bold text-sm md:text-base mb-1">{feature.title}</h3>
              <p className="text-slate-400 text-xs md:text-sm">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Areas Section
const ServiceAreasSection = () => {
  const [suburb, setSuburb] = useState("");
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const FREE_RADIUS_KM = 15;
  const FEE_PER_KM = 0.40;

  // Popular suburbs for quick selection grid
  const suburbs = [
    { name: "Thomastown", distance: 0 },
    { name: "Bundoora", distance: 5 },
    { name: "Mill Park", distance: 4 },
    { name: "Lalor", distance: 3 },
    { name: "Epping", distance: 5 },
    { name: "Preston", distance: 8 },
    { name: "Reservoir", distance: 6 },
    { name: "South Morang", distance: 7 },
    { name: "Doncaster", distance: 12 },
    { name: "Sunshine", distance: 18 },
    { name: "Footscray", distance: 20 },
    { name: "Melbourne CBD", distance: 18 }
  ];


  const calculateTravelFee = (distance) => {
    if (distance <= FREE_RADIUS_KM) return 0;
    return (distance - FREE_RADIUS_KM) * FEE_PER_KM;
  };

  // Fetch Google Places Autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setPredictions([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { query }
      });
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Autocomplete error:", error);
      setPredictions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSuburb(value);
    setShowSuggestions(true);
    setCalculatedFee(null);
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const selectPrediction = async (prediction) => {
    setSuburb(prediction.description || prediction.main_text);
    setShowSuggestions(false);
    setPredictions([]);
    await calculateFee(prediction.description || prediction.main_text);
  };

  const calculateFee = async (address) => {
    const searchAddress = address || suburb;
    if (!searchAddress.trim()) return;
    
    setLoading(true);
    setShowSuggestions(false);
    setPredictions([]);
    
    try {
      const response = await axios.post(`${API}/calculate-travel-fee`, {
        address: searchAddress
      });
      
      if (response.data.success) {
        setCalculatedFee({
          fee: response.data.fee,
          distance: response.data.distance_km,
          suburb: searchAddress,
          source: "google"
        });
      } else {
        setCalculatedFee({ 
          fee: calculateTravelFee(25), 
          distance: 25, 
          suburb: searchAddress, 
          unknown: true, 
          source: "estimate" 
        });
      }
    } catch (error) {
      console.error("Error calculating fee:", error);
      setCalculatedFee({ 
        fee: calculateTravelFee(25), 
        distance: 25, 
        suburb: searchAddress, 
        unknown: true, 
        source: "estimate" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="service-areas" className="section-spacing bg-slate-900/50" data-testid="service-areas-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Service <span className="gradient-text">Areas</span>
          </h2>
          <p className="text-slate-400 mb-8">
            Based in Thomastown, servicing Melbourne's North & West
          </p>
        </motion.div>

        {/* Distance Calculator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-6 max-w-md mx-auto mb-10"
          data-testid="distance-calculator"
        >
          <h3 className="font-bold text-lg mb-4 text-center">Calculate Travel Fee</h3>
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Enter your suburb"
                  value={suburb}
                  onChange={handleInputChange}
                  onFocus={() => setShowSuggestions(true)}
                  className="bg-slate-800 border-slate-700 pl-10"
                  data-testid="suburb-input"
                />
                {/* Google Places Autocomplete suggestions */}
                {showSuggestions && predictions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden z-50 max-h-64 overflow-y-auto shadow-xl">
                    {predictions.map((prediction, i) => (
                      <button
                        key={i}
                        onClick={() => selectPrediction(prediction)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center gap-3 border-b border-slate-700 last:border-0"
                      >
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0 text-sm">
                          <span className="font-medium">{prediction.main_text}</span>
                          {prediction.secondary_text && (
                            <span className="text-slate-400 ml-1 text-xs">{prediction.secondary_text}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {searchLoading && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg p-3 z-50">
                    <div className="flex items-center gap-2 text-slate-400">
                      <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Searching...</span>
                    </div>
                  </div>
                )}
              </div>
              <Button 
                onClick={() => calculateFee()}
                className="btn-primary-glow text-slate-900 px-6"
                data-testid="calculate-fee-btn"
                disabled={loading || !suburb.trim()}
              >
                {loading ? "..." : "Check"}
              </Button>
            </div>
          </div>
          
          {loading && (
            <div className="mt-4 p-4 bg-slate-800 rounded-lg text-center">
              <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-slate-400 text-sm">Calculating distance...</p>
            </div>
          )}
          
          {!loading && calculatedFee !== null && (
            <div className="mt-4 p-4 bg-slate-800 rounded-lg" data-testid="calculated-fee">
              <div className="text-center">
                <p className="text-slate-400 text-sm mb-1">
                  {calculatedFee.suburb} • {calculatedFee.distance}km from Thomastown
                  {calculatedFee.unknown && " (estimated)"}
                </p>
                {calculatedFee.fee === 0 ? (
                  <p className="text-green-400 font-bold text-lg">Free travel!</p>
                ) : (
                  <>
                    <p className="text-cyan-400 font-bold text-lg">
                      Travel fee: ${calculatedFee.fee.toFixed(2)}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      ({(calculatedFee.distance - FREE_RADIUS_KM).toFixed(1)}km × $0.40/km)
                    </p>
                  </>
                )}
                {calculatedFee.source === "google" && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Powered by Google Maps
                  </p>
                )}
              </div>
            </div>
          )}
          
          <p className="text-xs text-slate-500 mt-4 text-center">
            Free travel within {FREE_RADIUS_KM}km • ${FEE_PER_KM.toFixed(2)}/km beyond
          </p>
        </motion.div>

        {/* Area Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {suburbs.slice(0, 12).map((s, i) => {
            const fee = calculateTravelFee(s.distance);
            return (
              <div 
                key={i} 
                className={`service-area-card ${fee === 0 ? "free" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{s.name}</span>
                  {fee === 0 ? (
                    <span className="text-xs text-green-400">Free</span>
                  ) : (
                    <span className="text-xs text-slate-400">+${fee.toFixed(2)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          Don't see your area? Use the calculator above or contact us!
        </p>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5.0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [source, setSource] = useState("loading");
  const [businessName, setBusinessName] = useState("RainXpress Mobile Detailing");

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data.reviews || []);
        setRating(response.data.rating || 5.0);
        setTotalReviews(response.data.total_reviews || 0);
        setSource(response.data.source || "testimonials");
        setBusinessName(response.data.business_name || "RainXpress Mobile Detailing");
      } catch (error) {
        console.error("Error fetching reviews:", error);
        // Fallback reviews
        setReviews([
          { author: "Sarah M.", rating: 5, text: "Absolutely amazing service! My car looks brand new.", relative_time: "Recently" },
          { author: "James T.", rating: 5, text: "Best mobile detailing service in Melbourne!", relative_time: "Recently" },
          { author: "Michelle K.", rating: 5, text: "RainXpress is by far the best detailer I've used.", relative_time: "Recently" }
        ]);
        setSource("testimonials");
      }
    };
    fetchReviews();
  }, []);

  return (
    <section className="section-spacing bg-slate-950" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Customer <span className="gradient-text">Reviews</span>
          </h2>
          <div className="flex justify-center items-center gap-2 mb-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`w-6 h-6 ${i < Math.round(rating) ? 'text-yellow-400' : 'text-slate-600'}`} 
                  fill={i < Math.round(rating) ? "currentColor" : "none"} 
                />
              ))}
            </div>
            <span className="text-xl font-bold text-white">{rating.toFixed(1)}</span>
          </div>
          {totalReviews > 0 && (
            <p className="text-slate-400 text-sm">Based on {totalReviews} reviews</p>
          )}
        </motion.div>

        <div className={`grid grid-cols-1 ${reviews.length >= 2 ? 'md:grid-cols-2' : ''} ${reviews.length >= 3 ? 'md:grid-cols-3' : ''} gap-6 ${reviews.length === 1 ? 'max-w-lg mx-auto' : ''}`}>
          {reviews.slice(0, 3).map((review, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="review-card"
              data-testid={`review-${i}`}
            >
              <div className="flex items-center gap-1 mb-3">
                {[...Array(review.rating || 5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 text-yellow-400" fill="currentColor" />
                ))}
              </div>
              <p className="text-slate-300 mb-4 line-clamp-4">"{review.text}"</p>
              <div className="flex items-center gap-3 text-sm">
                {review.photo_uri && (
                  <img 
                    src={review.photo_uri} 
                    alt={review.author} 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <div className="flex-1 flex justify-between items-center">
                  <span className="font-medium text-white">{review.author}</span>
                  <span className="text-slate-500">{review.relative_time || review.date}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-slate-500 text-sm mt-8">
          {source === "google" ? (
            <a 
              href="https://share.google/jwoJAdp1uC4sM5nMr" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 hover:text-cyan-400 transition"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              View All Reviews on Google →
            </a>
          ) : (
            "Customer testimonials"
          )}
        </p>
      </div>
    </section>
  );
};

// Policies Section
const PoliciesSection = () => {
  return (
    <section id="terms" className="section-spacing bg-slate-900/50" data-testid="policies-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Terms & <span className="gradient-text">Conditions</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Requirements */}
          <div className="glass rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4 text-cyan-400">Key Requirements</h3>
            <ul className="space-y-3">
              {[
                "Provide safe, legal parking space",
                "Allow sufficient space around car (approx. 1m)",
                "Provide access to power (if required)",
                "Provide access to water (if required)",
                "Ensure vehicle is unlocked at arrival"
              ].map((item, i) => (
                <li key={i} className="policy-bullet text-slate-300 text-sm">{item}</li>
              ))}
            </ul>
          </div>

          {/* Customer Must */}
          <div className="glass rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4 text-cyan-400">Before Service</h3>
            <ul className="space-y-3">
              {[
                "Remove all personal belongings from interior & boot",
                "Remove all valuables",
                "24-hour cancellation policy"
              ].map((item, i) => (
                <li key={i} className="policy-bullet text-slate-300 text-sm">{item}</li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-4">
              *We are not responsible for personal items left inside the vehicle
            </p>
          </div>
        </div>

        {/* Accordion for detailed policies */}
        <div className="mt-8">
          <Accordion type="single" collapsible className="glass rounded-2xl overflow-hidden">
            <AccordionItem value="cancellation" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Cancellation Policy
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <ul className="space-y-2">
                  <li>• 48+ hours: Free reschedule</li>
                  <li>• 24-48 hours: Deposit retained</li>
                  <li>• Under 24 hours: 50% service fee may apply</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="condition" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Condition Disclosure
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p className="mb-2">Customers must accurately declare:</p>
                <ul className="space-y-1">
                  <li>• Excessive pet hair, sand/mud buildup</li>
                  <li>• Mold, biohazard, vomit, bodily fluids</li>
                  <li>• Heavy stains, smoke odor</li>
                </ul>
                <p className="mt-2 text-cyan-400 font-medium">Undisclosed heavy conditions may result in additional charges or rescheduling.</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="payment" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Payment Policy
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <ul className="space-y-2">
                  <li>• Payment due immediately after service</li>
                  <li>• Card & cash accepted</li>
                  <li>• No "pay later" options</li>
                  <li>• <span className="text-cyan-400">A deposit may be requested to confirm your booking</span></li>
                </ul>
                <p className="mt-4 text-slate-300 bg-slate-800/50 p-3 rounded-lg">
                  <span className="text-cyan-400 font-medium">Unsure about your booking?</span> Text, call or email us and we will respond same day.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="weather" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Weather & Safety
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p className="mb-2">Service may require rescheduling for safety and quality reasons due to weather.</p>
                <p className="mb-2 text-cyan-400">In case of rain or thunderstorm, we may be able to do service if there is shade that protects from rain.</p>
                <p className="mt-2">We will refuse service if:</p>
                <ul className="space-y-1 mt-2">
                  <li>• Unsafe location</li>
                  <li>• Aggressive behaviour</li>
                  <li>• Hazardous materials</li>
                  <li>• Undeclared biohazard contamination</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="satisfaction" className="border-slate-700">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Satisfaction Guarantee
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p>If you are not fully satisfied, notify us on site and we will address concerns immediately.</p>
                <p className="mt-3 text-slate-300">
                  We are not responsible for personal items left inside the vehicle.
                </p>
                <p className="mt-2 text-slate-300">
                  We are not responsible for pre-existing damage, worn trim, loose panels or fragile components.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="photo-consent" className="border-slate-700 border-b-0">
              <AccordionTrigger className="px-6 hover:no-underline text-left">
                Photo & Media Consent
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-400 text-sm">
                <p>We may take before and after photos for quality control and marketing.</p>
                <p className="mt-2 text-cyan-400">
                  License plates and personal details will not be displayed without permission.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Coming Soon Trade Section
const TradeSectionComingSoon = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/trade-inquiries?email=${encodeURIComponent(email)}`);
      setSubmitted(true);
      toast.success("Thanks! We'll notify you when trade packages launch.");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section className="section-spacing bg-slate-950" data-testid="trade-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 md:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute top-4 right-4 bg-cyan-400 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
            Coming Soon
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Trade & Business <span className="gradient-text">Packages</span>
          </h2>
          
          <p className="text-slate-400 mb-6">
            Special rates for Uber drivers, tradies, plumbers, and business fleets.<br />
            <span className="text-cyan-400">We bring our own water & power - visit you anywhere!</span>
          </p>

          {!submitted ? (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-800 border-slate-700 flex-1"
                data-testid="trade-email-input"
              />
              <Button 
                type="submit" 
                className="btn-primary-glow text-slate-900 font-semibold px-6"
                data-testid="trade-notify-btn"
              >
                Notify Me
              </Button>
            </form>
          ) : (
            <p className="text-green-400 font-medium">You're on the list! We'll be in touch soon.</p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// Account Benefits Section
const AccountBenefitsSection = () => {
  const navigate = useNavigate();
  
  const benefits = [
    { icon: Award, title: "Loyalty Rewards", desc: "5 services = 50% OFF, 10 services = FREE!" },
    { icon: Gift, title: "Referral Bonus", desc: "Share your code - both get 15% off!" },
    { icon: Calendar, title: "Easy Booking", desc: "Pre-filled details, faster checkout" },
    { icon: Clock, title: "Booking History", desc: "View past & upcoming appointments" },
    { icon: Star, title: "Exclusive Offers", desc: "Members-only discounts & early access" },
    { icon: Cake, title: "Birthday Rewards", desc: "Special treats on your special day!" }
  ];

  return (
    <section className="section-spacing bg-gradient-to-b from-slate-900 to-slate-950" data-testid="account-benefits-section">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Why Create an <span className="gradient-text">Account?</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Join the RainXpress family and unlock exclusive rewards and easier booking management
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-10">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="glass rounded-xl p-4 md:p-6 text-center hover:border-cyan-400/30 transition-all"
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-400/10 flex items-center justify-center">
                <benefit.icon className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold mb-1 text-sm md:text-base">{benefit.title}</h3>
              <p className="text-slate-400 text-xs md:text-sm">{benefit.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Button 
            onClick={() => navigate('/account')}
            className="btn-primary-glow text-slate-900 font-semibold px-8 py-3 rounded-full"
          >
            <Users className="w-5 h-5 mr-2" />
            Create Free Account
          </Button>
          <p className="text-slate-500 text-sm mt-3">It's free and takes less than a minute</p>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const { setShowBookingModal } = useBooking();

  return (
    <section className="section-spacing bg-gradient-to-b from-slate-950 to-slate-900" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Book Your Detail <span className="gradient-text">Today</span>
          </h2>
          
          <p className="text-lg text-slate-400 mb-6 max-w-2xl mx-auto">
            Clean car anywhere, everywhere.
          </p>

          <Button 
            onClick={() => setShowBookingModal(true)}
            className="btn-primary-glow text-slate-900 font-bold px-10 py-5 rounded-full text-lg"
            data-testid="final-cta-book-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Now
          </Button>

          <p className="text-slate-400 text-sm mt-6 max-w-xl mx-auto">
            Unsure of what to book or need an accurate price quotation with travel fee?{' '}
            <a href="tel:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">Call</a> or{' '}
            <a href="sms:0478054648" className="text-cyan-400 hover:text-cyan-300 font-medium">text now</a> and we respond same day.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer id="contact" className="bg-slate-950 border-t border-slate-800 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/logo.png" 
                alt="RainXpress Logo" 
                className="h-12 w-12 rounded-full object-cover"
              />
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Melbourne Mobile Car Detailing.
            </p>
            <p className="text-xs text-slate-500">
              We may take before and after photos for quality control and marketing. 
              License plates and personal details will not be displayed without permission.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold text-lg mb-4">Contact</h3>
            <div className="space-y-3">
              <a href="tel:0478054648" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition">
                <Phone className="w-4 h-4" />
                0478 054 648
              </a>
              <a href="mailto:hello.rainxpress@gmail.com" className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition">
                <Mail className="w-4 h-4" />
                hello.rainxpress@gmail.com
              </a>
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4" />
                Thomastown, VIC
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <div className="space-y-2">
              <a href="#packages" className="block text-slate-400 hover:text-cyan-400 transition">Packages</a>
              <a href="#service-areas" className="block text-slate-400 hover:text-cyan-400 transition">Service Areas</a>
              <a href="#policies-section" className="block text-slate-400 hover:text-cyan-400 transition">Policies</a>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
          <p>&copy; {new Date().getFullYear()} RainXpress. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Booking Modal
const BookingModal = () => {
  const { 
    showBookingModal, setShowBookingModal,
    vehicleType, setVehicleType,
    selectedPackage, setSelectedPackage,
    addons, setAddons,
    discountCode, setDiscountCode
  } = useBooking();

  const [step, setStep] = useState(1);
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreeToCancellation, setAgreeToCancellation] = useState(false);
  const [carPhotos, setCarPhotos] = useState([]);
  const [carAtHome, setCarAtHome] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [discountValidation, setDiscountValidation] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [blockedSlotsForDate, setBlockedSlotsForDate] = useState([]);
  const [loadingBlockedSlots, setLoadingBlockedSlots] = useState(false);
  const [fullyBlockedDates, setFullyBlockedDates] = useState([]);
  const fileInputRef = useRef(null);

  // Fetch all fully blocked dates when modal opens
  useEffect(() => {
    const fetchFullyBlockedDates = async () => {
      if (!showBookingModal) return;
      try {
        const response = await axios.get(`${API}/blocked-dates-full`);
        setFullyBlockedDates(response.data.blocked_dates || []);
      } catch (error) {
        console.error("Failed to fetch blocked dates:", error);
        setFullyBlockedDates([]);
      }
    };
    fetchFullyBlockedDates();
  }, [showBookingModal]);

  // Fetch blocked slots when date changes
  useEffect(() => {
    const fetchBlockedSlots = async () => {
      if (!date) {
        setBlockedSlotsForDate([]);
        return;
      }
      setLoadingBlockedSlots(true);
      try {
        const dateStr = format(date, "yyyy-MM-dd");
        const response = await axios.get(`${API}/blocked-slots/${dateStr}`);
        setBlockedSlotsForDate(response.data.blocked_slots || []);
      } catch (error) {
        console.error("Failed to fetch blocked slots:", error);
        setBlockedSlotsForDate([]);
      } finally {
        setLoadingBlockedSlots(false);
      }
    };
    fetchBlockedSlots();
  }, [date]);

  // Find next available date
  const findNextAvailableDate = async () => {
    setLoadingBlockedSlots(true);
    try {
      const response = await axios.get(`${API}/next-available-date`);
      if (response.data.date) {
        const nextDate = new Date(response.data.date);
        setDate(nextDate);
        toast.success(`Next available date: ${format(nextDate, "PPP")}`);
      } else {
        toast.info("No blocked dates found - all dates are available!");
      }
    } catch (error) {
      toast.error("Failed to find next available date");
    } finally {
      setLoadingBlockedSlots(false);
    }
  };

  // Validate discount code via API
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }
    
    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${API}/validate-discount?code=${encodeURIComponent(discountCode)}`);
      setDiscountValidation(response.data);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setDiscountValidation({ valid: false, message: "Failed to validate code" });
      toast.error("Failed to validate discount code");
    } finally {
      setValidatingDiscount(false);
    }
  };

  // Clear validation when discount code changes
  const handleDiscountCodeChange = (value) => {
    setDiscountCode(value);
    setDiscountValidation(null); // Reset validation when code changes
  };

  const { user, register } = useAuth();
  
  // Check for birthday discount
  const [birthdayDiscount, setBirthdayDiscount] = useState(null);
  
  useEffect(() => {
    const checkBirthdayDiscount = async () => {
      if (user?.id) {
        try {
          const response = await axios.get(`${API}/check-birthday-discount/${user.id}`);
          if (response.data.eligible) {
            setBirthdayDiscount(response.data);
          }
        } catch (error) {
          console.error("Failed to check birthday discount:", error);
        }
      }
    };
    checkBirthdayDiscount();
  }, [user?.id]);

  const [formData, setFormData] = useState({
    customer_name: "",
    phone: "",
    email: "",
    address: "",
    car_address: "",
    make_model: "",
    car_condition: "",
    special_instructions: "",
    preferred_time: "",
    preferred_contact: "text" // text or email
  });

  // Pre-fill form with user data if logged in
  useEffect(() => {
    if (user && showBookingModal) {
      setFormData(prev => ({
        ...prev,
        customer_name: prev.customer_name || user.name || "",
        phone: prev.phone || user.phone || "",
        email: prev.email || user.email || "",
        address: prev.address || user.home_address || ""
      }));
    }
  }, [user, showBookingModal]);

  const times = TIME_SLOTS;

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + carPhotos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setCarPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setCarPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const calculateTotal = () => {
    if (!selectedPackage) return { package: 0, addons: 0, total: 0, discount: 0, discountType: null };
    const pkgPrice = PACKAGES[selectedPackage]?.basePrices[vehicleType] || 0;
    const addonsPrice = addons.reduce((sum, id) => {
      const addon = ADDONS.find(a => a.id === id);
      return sum + (addon?.price || 0);
    }, 0);
    let total = pkgPrice + addonsPrice;
    let discountAmount = 0;
    let discountType = null;
    
    // Birthday discount takes priority (50% off during birthday month)
    if (birthdayDiscount?.eligible && !discountCode) {
      discountAmount = total * 0.5;
      total = total * 0.5;
      discountType = "50% Birthday Reward";
    }
    // Apply validated discount from API
    else if (discountValidation?.valid && discountValidation.discount) {
      const disc = discountValidation.discount;
      if (disc.type === "percent") {
        discountAmount = total * (disc.value / 100);
        total = total - discountAmount;
        discountType = `${disc.value}% discount code`;
      } else if (disc.type === "fixed") {
        discountAmount = Math.min(disc.value, total);
        total = Math.max(0, total - disc.value);
        discountType = `$${disc.value} off code`;
      }
    }
    // Fallback for loyalty codes (LOYAL50, FREESERVICE) - these are system codes
    else if (discountCode.toUpperCase() === "LOYAL50" && user?.loyalty_points >= 5) {
      discountAmount = total * 0.5;
      total *= 0.5;
      discountType = "50% Loyalty Reward";
    } else if (discountCode.toUpperCase() === "FREESERVICE" && user?.free_services_earned > 0) {
      discountAmount = total;
      total = 0;
      discountType = "Free Service Reward";
    }
    // Apply referral discount if user has it available and no other discount
    else if (user?.referral_discount_available && !discountCode) {
      discountAmount = total * 0.15;
      total *= 0.85; // 15% off from referral
      discountType = "15% Referral Reward";
    }
    
    return { package: pkgPrice, addons: addonsPrice, total, discount: discountAmount, discountType };
  };

  const validateReferralCode = async (code) => {
    if (!code || code.length < 6) {
      setReferralValid(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/validate-referral/${code}`);
      setReferralValid(response.data);
    } catch (error) {
      setReferralValid({ valid: false });
    }
  };

  const toggleAddon = (addonId) => {
    setAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSubmit = async () => {
    if (!formData.customer_name || !formData.phone || !formData.address || !date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate car address if not at home
    if (!carAtHome && !formData.car_address) {
      toast.error("Please enter the car location address");
      return;
    }

    if (!agreeToTerms || !agreeToCancellation) {
      toast.error("Please agree to all terms and policies to continue");
      return;
    }

    // Validate account creation fields
    if (createAccount) {
      if (!formData.email) {
        toast.error("Email is required to create an account");
        return;
      }
      if (!accountPassword || accountPassword.length < 6) {
        toast.error("Password must be at least 6 characters");
        return;
      }
    }

    setLoading(true);
    try {
      // If user wants to create an account, do that first
      let newUserId = null;
      if (createAccount && formData.email && accountPassword) {
        try {
          const regResponse = await register(
            formData.customer_name,
            formData.email,
            accountPassword,
            formData.phone
          );
          newUserId = regResponse.user?.id;
          toast.success("Account created! Check your email to verify.");
        } catch (regError) {
          if (regError.response?.status === 400) {
            toast.error(regError.response?.data?.detail || "Email already registered. Please login instead.");
            setLoading(false);
            return;
          }
          // Continue with booking even if registration fails for other reasons
          console.error("Registration error:", regError);
        }
      }

      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        car_at_home: carAtHome,
        car_address: carAtHome ? null : formData.car_address,
        vehicle_type: vehicleType,
        package: selectedPackage,
        addons,
        discount_code: discountCode,
        referral_code: referralValid?.valid ? referralCode : null,
        preferred_date: format(date, "yyyy-MM-dd"),
        origin_url: window.location.origin,
        has_photos: carPhotos.length > 0,
        user_id: newUserId || user?.id
      });

      const bookingId = response.data.booking?.id;

      // Upload photos if any
      if (carPhotos.length > 0 && bookingId) {
        const photoFormData = new FormData();
        photoFormData.append('booking_id', bookingId);
        carPhotos.forEach(photo => {
          photoFormData.append('files', photo.file);
        });
        try {
          await axios.post(`${API}/upload-photos`, photoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (photoError) {
          console.error("Photo upload error:", photoError);
          // Continue with booking even if photo upload fails
        }
      }

      // Handle booking response
      if (response.data.status === "pending" || response.data.booking_id) {
        // New flow: No deposit required upfront
        toast.success("Booking request submitted! We'll contact you to confirm your time and send payment details.");
        setShowBookingModal(false);
        resetModal();
        setLoading(false);
      } else {
        console.error("Unexpected response:", response.data);
        toast.error("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error("Booking error:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(1);
    setAgreeToTerms(false);
    setAgreeToCancellation(false);
    setCarPhotos([]);
    setCarAtHome(true);
    setReferralCode("");
    setReferralValid(null);
    setFormData({
      customer_name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
      address: user?.home_address || "",
      car_address: "",
      make_model: "",
      car_condition: "",
      special_instructions: "",
      preferred_time: "",
      preferred_contact: "text"
    });
    setDate(null);
  };

  useEffect(() => {
    if (!showBookingModal) {
      resetModal();
    }
  }, [showBookingModal]);

  const prices = calculateTotal();

  return (
    <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            Book Your Detail
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Step {step} of 3
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Step 1: Package & Vehicle */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-3 block">Select Vehicle Type</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: "small", label: "Small" },
                    { id: "medium", label: "SUV/Med" },
                    { id: "large", label: "Large/Van" },
                    { id: "xl", label: "XL/Truck" }
                  ].map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVehicleType(v.id)}
                      className={`py-3 px-4 rounded-lg border transition ${
                        vehicleType === v.id 
                          ? "border-cyan-400 bg-cyan-400/10 text-cyan-400" 
                          : "border-slate-700 text-slate-400 hover:border-slate-600"
                      }`}
                      data-testid={`modal-vehicle-${v.id}`}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">Select Package</Label>
                <div className="space-y-3">
                  {Object.entries(PACKAGES).map(([key, pkg]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPackage(key)}
                      className={`w-full p-4 rounded-lg border text-left transition ${
                        selectedPackage === key 
                          ? "border-cyan-400 bg-cyan-400/10" 
                          : "border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`modal-package-${key}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{pkg.name}</span>
                          {pkg.popular && (
                            <span className="ml-2 text-xs bg-cyan-400 text-slate-900 px-2 py-0.5 rounded-full">
                              Popular
                            </span>
                          )}
                        </div>
                        <span className="text-cyan-400 font-bold">${pkg.basePrices[vehicleType]}</span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{pkg.tagline}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">Optional Add-ons</Label>
                <div className="grid grid-cols-2 gap-2">
                  {ADDONS.map((addon) => (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddon(addon.id)}
                      className={`p-3 rounded-lg border text-left text-sm transition ${
                        addons.includes(addon.id) 
                          ? "border-cyan-400 bg-cyan-400/10" 
                          : "border-slate-700 hover:border-slate-600"
                      }`}
                      data-testid={`addon-${addon.id}`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300">{addon.name}</span>
                        <span className="text-cyan-400">+${addon.price}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Birthday Discount Banner */}
              {birthdayDiscount?.eligible && (
                <div className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl p-4 animate-pulse-slow">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">🎂</div>
                    <div>
                      <p className="text-pink-400 font-bold">Happy Birthday!</p>
                      <p className="text-sm text-slate-300">Enjoy <span className="text-pink-400 font-bold">50% OFF</span> your service this month!</p>
                      <p className="text-xs text-slate-400">Valid until {birthdayDiscount.valid_until}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium mb-3 block">Preferred Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-slate-800 border-slate-700 hover:bg-slate-700"
                      data-testid="date-picker-trigger"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                    <CalendarComponent
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (d < today) return true;
                        // Check if date is fully blocked
                        const dateStr = format(d, "yyyy-MM-dd");
                        return fullyBlockedDates.some(b => b.date === dateStr);
                      }}
                      modifiers={{
                        blocked: (d) => {
                          const dateStr = format(d, "yyyy-MM-dd");
                          return fullyBlockedDates.some(b => b.date === dateStr);
                        }
                      }}
                      modifiersClassNames={{
                        blocked: "line-through text-red-400 opacity-50"
                      }}
                      className="bg-slate-800"
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Preferred Time</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={findNextAvailableDate}
                    disabled={loadingBlockedSlots}
                    className="text-xs bg-cyan-600/20 border-cyan-500/30 hover:bg-cyan-600/30 text-cyan-400"
                  >
                    {loadingBlockedSlots ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Calendar className="w-3 h-3 mr-1" />
                    )}
                    Next Available Date
                  </Button>
                </div>
                
                {/* Show blocked slots warning */}
                {date && blockedSlotsForDate.length > 0 && (
                  <div className="mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-amber-400 text-sm font-medium">Some times unavailable on this date</p>
                        {blockedSlotsForDate.some(s => s.time === null) ? (
                          <p className="text-amber-300/80 text-xs mt-1">
                            {blockedSlotsForDate[0]?.reason || "Fully booked"}
                          </p>
                        ) : (
                          <div className="text-amber-300/80 text-xs mt-1 space-y-1">
                            {(() => {
                              // Group blocked times into ranges
                              const blockedTimes = blockedSlotsForDate.map(s => s.time).filter(Boolean);
                              const sortedTimes = [...new Set(blockedTimes)].sort((a, b) => 
                                TIME_SLOTS.indexOf(a) - TIME_SLOTS.indexOf(b)
                              );
                              const ranges = [];
                              let rangeStart = sortedTimes[0];
                              let rangeEnd = sortedTimes[0];
                              
                              for (let i = 1; i < sortedTimes.length; i++) {
                                const currentIndex = TIME_SLOTS.indexOf(sortedTimes[i]);
                                const prevIndex = TIME_SLOTS.indexOf(rangeEnd);
                                if (currentIndex === prevIndex + 1) {
                                  rangeEnd = sortedTimes[i];
                                } else {
                                  ranges.push({ start: rangeStart, end: rangeEnd });
                                  rangeStart = sortedTimes[i];
                                  rangeEnd = sortedTimes[i];
                                }
                              }
                              ranges.push({ start: rangeStart, end: rangeEnd });
                              
                              const reason = blockedSlotsForDate[0]?.reason || "Booked";
                              return ranges.map((range, idx) => (
                                <p key={idx}>
                                  {range.start === range.end ? range.start : `${range.start} - ${range.end}`}
                                  {idx === 0 && ` - ${reason}`}
                                </p>
                              ));
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <Select 
                  value={formData.preferred_time} 
                  onValueChange={(v) => setFormData({...formData, preferred_time: v})}
                  disabled={date && blockedSlotsForDate.some(s => s.time === null)}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="time-select">
                    <SelectValue placeholder={
                      date && blockedSlotsForDate.some(s => s.time === null) 
                        ? (blockedSlotsForDate[0]?.reason || "Fully booked")
                        : "Select time"
                    } />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                    {times.map((time) => {
                      const isBlocked = blockedSlotsForDate.some(s => s.time === time);
                      const blockReason = blockedSlotsForDate.find(s => s.time === time)?.reason;
                      return (
                        <SelectItem 
                          key={time} 
                          value={time}
                          disabled={isBlocked}
                          className={isBlocked ? "text-slate-500 line-through" : ""}
                        >
                          {time}
                          {isBlocked && ` (${blockReason || "Booked"})`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Time Request Note */}
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <p className="text-sm text-slate-300">
                  <span className="text-cyan-400 font-medium">Note:</span> This is a time request only. We will contact you via your preferred method to confirm the finalised time and cost (after calculating travel and service duration).
                </p>
                
                <div className="mt-3">
                  <Label className="text-sm font-medium mb-2 block">Preferred Contact Method</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="text"
                        checked={formData.preferred_contact === "text"}
                        onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                        className="w-4 h-4 text-cyan-400 bg-slate-800 border-slate-600"
                        data-testid="contact-text-radio"
                      />
                      <span className="text-slate-300">Text (SMS)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="preferred_contact"
                        value="email"
                        checked={formData.preferred_contact === "email"}
                        onChange={(e) => setFormData({...formData, preferred_contact: e.target.value})}
                        className="w-4 h-4 text-cyan-400 bg-slate-800 border-slate-600"
                        data-testid="contact-email-radio"
                      />
                      <span className="text-slate-300">Email</span>
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Discount Code (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter code"
                    value={discountCode}
                    onChange={(e) => handleDiscountCodeChange(e.target.value)}
                    className={`bg-slate-800 border-slate-700 flex-1 ${
                      discountValidation?.valid ? 'border-green-500' : 
                      discountValidation && !discountValidation.valid ? 'border-red-500' : ''
                    }`}
                    data-testid="discount-code-input"
                  />
                  <Button
                    type="button"
                    onClick={validateDiscountCode}
                    disabled={!discountCode.trim() || validatingDiscount}
                    className="bg-cyan-600 hover:bg-cyan-500 disabled:bg-slate-700 disabled:text-slate-500"
                    data-testid="apply-discount-btn"
                  >
                    {validatingDiscount ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </div>
                {discountValidation?.valid && (
                  <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {discountValidation.message}
                  </p>
                )}
                {discountValidation && !discountValidation.valid && (
                  <p className="text-red-400 text-xs mt-1">{discountValidation.message}</p>
                )}
              </div>

              {/* Available Rewards Section */}
              {user && (
                <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <p className="text-sm font-medium text-cyan-400 mb-2 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Your Available Rewards
                  </p>
                  {user.loyalty_points >= 5 && user.loyalty_points < 10 ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-300">50% Off Available!</p>
                        <p className="text-xs text-slate-500">Enter code: <span className="text-cyan-400 font-mono">LOYAL50</span></p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscountCode('LOYAL50')}
                        className="text-xs bg-cyan-600 hover:bg-cyan-500 px-3 py-1 rounded"
                      >
                        Apply
                      </button>
                    </div>
                  ) : user.free_services_earned > 0 ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-yellow-400">FREE Service Available!</p>
                        <p className="text-xs text-slate-500">Enter code: <span className="text-yellow-400 font-mono">FREESERVICE</span></p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDiscountCode('FREESERVICE')}
                        className="text-xs bg-yellow-600 hover:bg-yellow-500 px-3 py-1 rounded"
                      >
                        Apply
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">
                      {5 - (user.loyalty_points || 0)} more bookings until 50% off! Keep going!
                    </p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-sm font-medium mb-2 block">Referral Code (optional)</Label>
                <Input
                  placeholder="Got a friend's code?"
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.toUpperCase());
                    validateReferralCode(e.target.value);
                  }}
                  className={`bg-slate-800 border-slate-700 ${referralValid?.valid ? 'border-green-500' : ''}`}
                  data-testid="referral-code-input"
                />
                {referralValid?.valid && (
                  <p className="text-green-400 text-xs mt-1">
                    Referred by {referralValid.referrer_name}
                  </p>
                )}
                {referralValid && !referralValid.valid && referralCode.length >= 6 && (
                  <p className="text-red-400 text-xs mt-1">Invalid referral code</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Full Name *</Label>
                <Input
                  placeholder="Your name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="customer-name-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Phone *</Label>
                <Input
                  placeholder="0400 000 000"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Home Address *</Label>
                <Input
                  placeholder="123 Street Name, Suburb"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="address-input"
                />
              </div>

              {/* Car Location */}
              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="car-at-home" 
                    checked={carAtHome}
                    onCheckedChange={setCarAtHome}
                    data-testid="car-at-home-checkbox"
                  />
                  <label htmlFor="car-at-home" className="text-sm text-slate-300 cursor-pointer">
                    Car is at my home address
                  </label>
                </div>
                
                {!carAtHome && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block text-cyan-400">Car Location Address *</Label>
                    <Input
                      placeholder="Where is the car located?"
                      value={formData.car_address}
                      onChange={(e) => setFormData({...formData, car_address: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      data-testid="car-address-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Travel fee will be calculated from this location
                    </p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Vehicle Make & Model</Label>
                <Input
                  placeholder="e.g. Toyota Camry 2020"
                  value={formData.make_model}
                  onChange={(e) => setFormData({...formData, make_model: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  data-testid="make-model-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Car Condition</Label>
                <Select 
                  value={formData.car_condition} 
                  onValueChange={(v) => setFormData({...formData, car_condition: v})}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="condition-select">
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="light">Light - Regular maintenance</SelectItem>
                    <SelectItem value="moderate">Moderate - Some dirt/stains</SelectItem>
                    <SelectItem value="heavy">Heavy - Significant cleaning needed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Special Instructions (optional)</Label>
                <Textarea
                  placeholder="Any specific areas to focus on? Pet hair? Etc."
                  value={formData.special_instructions}
                  onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                  className="bg-slate-800 border-slate-700"
                  rows={3}
                  data-testid="instructions-textarea"
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Upload Car Photos (optional)</Label>
                <p className="text-xs text-slate-500 mb-3">Upload up to 5 photos for more accurate quotation</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="photo-input"
                />
                <div className="flex flex-wrap gap-2 mb-3">
                  {carPhotos.map((photo, index) => (
                    <div key={index} className="relative w-20 h-20 rounded-lg overflow-hidden bg-slate-800">
                      <img src={photo.preview} alt={`Car ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {carPhotos.length < 5 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 flex flex-col items-center justify-center text-slate-500 hover:border-cyan-400 hover:text-cyan-400 transition"
                    >
                      <Camera className="w-5 h-5 mb-1" />
                      <span className="text-xs">Add</span>
                    </button>
                  )}
                </div>
                {carPhotos.length === 0 && (
                  <p className="text-xs text-yellow-400 bg-yellow-400/10 p-2 rounded-lg">
                    <span className="font-medium">Note:</span> If no photos are uploaded, final cost may change depending on the condition of your car.
                  </p>
                )}
              </div>

              {/* Account Creation Option - Only show if not logged in and email provided */}
              {!user && formData.email && (
                <div className="bg-gradient-to-r from-cyan-400/10 to-teal-500/10 border border-cyan-400/30 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Checkbox 
                      id="create-account" 
                      checked={createAccount}
                      onCheckedChange={setCreateAccount}
                      data-testid="create-account-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="create-account" className="text-sm font-medium text-cyan-400 cursor-pointer block mb-1">
                        Create an account with these details
                      </label>
                      <p className="text-xs text-slate-400">
                        Earn loyalty rewards (5 services = 50% OFF, 10 = FREE!), manage bookings easily, and get exclusive offers
                      </p>
                    </div>
                  </div>
                  
                  {createAccount && (
                    <div className="mt-3 ml-7">
                      <Label className="text-xs text-slate-400 mb-1 block">Create a password</Label>
                      <Input
                        type="password"
                        placeholder="Choose a password (min 6 characters)"
                        value={accountPassword}
                        onChange={(e) => setAccountPassword(e.target.value)}
                        className="bg-slate-800 border-slate-700"
                        data-testid="account-password-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Guest Checkout Benefits Reminder */}
              {!user && !createAccount && formData.email && (
                <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-400">
                    <span className="text-cyan-400 font-medium">Booking as guest?</span> You'll miss out on loyalty rewards (FREE services!) and easier booking management.{' '}
                    <button 
                      type="button"
                      onClick={() => setCreateAccount(true)}
                      className="text-cyan-400 underline hover:text-cyan-300"
                    >
                      Create account instead
                    </button>
                  </p>
                </div>
              )}

              {/* Summary */}
              <div className="bg-slate-800 rounded-lg p-4 mt-6">
                <h4 className="font-medium mb-3">Order Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{PACKAGES[selectedPackage]?.name}</span>
                    <span>${prices.package}</span>
                  </div>
                  {prices.addons > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Add-ons</span>
                      <span>${prices.addons}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-700 pt-2 flex justify-between font-bold">
                    <span>Total</span>
                    <span className="text-cyan-400">${prices.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="space-y-3 mt-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms-agreement"
                    checked={agreeToTerms}
                    onCheckedChange={setAgreeToTerms}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                    data-testid="terms-checkbox"
                  />
                  <label 
                    htmlFor="terms-agreement" 
                    className="text-sm text-slate-400 cursor-pointer"
                  >
                    I have read and agree to the <a href="/terms" onClick={(e) => { e.preventDefault(); window.open('/terms', '_blank'); }} className="text-cyan-400 hover:underline">terms & conditions of booking</a> *
                  </label>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="cancellation-agreement"
                    checked={agreeToCancellation}
                    onCheckedChange={setAgreeToCancellation}
                    className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-400 data-[state=checked]:border-cyan-400"
                    data-testid="cancellation-checkbox"
                  />
                  <label 
                    htmlFor="cancellation-agreement" 
                    className="text-sm text-slate-400 cursor-pointer"
                  >
                    I have read and agree to the <a href="/terms" onClick={(e) => { e.preventDefault(); window.open('/terms', '_blank'); }} className="text-cyan-400 hover:underline">cancellation policies</a> *
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <Button 
                variant="outline" 
                onClick={() => setStep(step - 1)}
                className="flex-1 border-slate-700 hover:bg-slate-800"
                data-testid="back-btn"
              >
                Back
              </Button>
            )}
            {step < 3 ? (
              <Button 
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !selectedPackage}
                className="flex-1 btn-primary-glow text-slate-900"
                data-testid="next-btn"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 btn-primary-glow text-slate-900"
                data-testid="submit-booking-btn"
              >
                {loading ? "Processing..." : "Request Booking"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Discount Popup
const DiscountPopup = () => {
  const { showDiscountPopup, setShowDiscountPopup, popupType } = useBooking();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const discountInfo = {
    normal_10: { title: "Get 10% Off", subtitle: "Your First Detail" },
    exit_15: { title: "Wait! Get 15% Off", subtitle: "Before You Go" },
    review_20: { title: "$20 Off", subtitle: "For Your Honest Review" }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscribers`, {
        email,
        discount_type: popupType,
        source: "popup"
      });
      
      if (response.data.already_subscribed) {
        toast.error("This email has already received a discount code.");
        setLoading(false);
        return;
      }
      
      setSubmitted(true);
      toast.success("Check your email for your discount code!");
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const info = discountInfo[popupType] || discountInfo.normal_10;

  return (
    <Dialog open={showDiscountPopup} onOpenChange={setShowDiscountPopup}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-md" data-testid="discount-popup">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            <span className="gradient-text">{info.title}</span>
          </DialogTitle>
          <DialogDescription className="text-center text-lg text-slate-300">
            {info.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {!submitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-800 border-slate-700"
                data-testid="popup-email-input"
              />
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn-primary-glow text-slate-900 font-semibold py-3"
                data-testid="popup-submit-btn"
              >
                {loading ? "Sending..." : "Get My Discount"}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                No spam. Unsubscribe anytime.
              </p>
            </form>
          ) : (
            <div className="text-center space-y-4">
              <div className="bg-slate-800 rounded-lg p-6">
                <Mail className="w-12 h-12 text-cyan-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-white mb-2">Check Your Email!</p>
                <p className="text-sm text-slate-400">
                  We've sent your exclusive discount code to <span className="text-cyan-400">{email}</span>
                </p>
              </div>
              <p className="text-sm text-slate-500">
                Didn't receive it? Check your spam folder or try again.
              </p>
              <Button 
                onClick={() => setShowDiscountPopup(false)}
                className="btn-primary-glow text-slate-900"
                data-testid="popup-close-btn"
              >
                Got It!
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Booking Success Page
const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [bookingDetails, setBookingDetails] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        let attempts = 0;
        const maxAttempts = 5;
        
        const poll = async () => {
          const response = await axios.get(`${API}/checkout/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            setBookingDetails(response.data);
          } else if (response.data.status === "expired" || attempts >= maxAttempts) {
            setStatus("error");
          } else {
            attempts++;
            setTimeout(poll, 2000);
          }
        };
        
        poll();
      } catch (error) {
        console.error("Error checking payment:", error);
        setStatus("error");
      }
    };

    checkPayment();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="booking-success-page">
      <div className="max-w-md w-full glass rounded-2xl p-8 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
            <p className="text-slate-400">Please wait while we confirm your booking.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-green-400">Booking Request Received!</h1>
            <p className="text-slate-400 mb-6">
              We'll be in touch shortly to confirm your appointment and send payment details.
            </p>
            <a 
              href="/"
              className="inline-block btn-primary-glow text-slate-900 font-semibold px-6 py-3 rounded-full"
            >
              Return Home
            </a>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-red-400">Payment Issue</h1>
            <p className="text-slate-400 mb-6">
              There was an issue processing your payment. Please try again or contact us.
            </p>
            <a 
              href="/"
              className="inline-block btn-secondary font-semibold px-6 py-3 rounded-full"
            >
              Return Home
            </a>
          </>
        )}
      </div>
    </div>
  );
};

// Packages Page (separate page for Packages navigation)
const PackagesPage = () => {
  const { vehicleType, setVehicleType, setSelectedPackage, setShowBookingModal } = useBooking();

  const handleBookPackage = (packageKey) => {
    setSelectedPackage(packageKey);
    setShowBookingModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="packages-page">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Our <span className="gradient-text">Packages</span>
            </h1>
            <p className="text-slate-400 text-lg mb-8">
              Professional mobile car detailing packages tailored to your needs
            </p>
            
            {/* Vehicle Selector */}
            <div className="flex flex-wrap justify-center gap-2 md:gap-4 mb-12">
              {[
                { id: "small", label: "Small Car", icon: Car },
                { id: "medium", label: "SUV / Med", icon: Car },
                { id: "large", label: "Large / Van", icon: Truck },
                { id: "xl", label: "XL / Truck", icon: Truck }
              ].map((v) => (
                <button
                  key={v.id}
                  onClick={() => setVehicleType(v.id)}
                  className={`vehicle-selector-btn flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all ${
                    vehicleType === v.id ? "active" : ""
                  }`}
                >
                  <v.icon className="w-5 h-5" />
                  {v.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Packages Detail */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-8">
            {Object.entries(PACKAGES).map(([key, pkg], index) => {
              const price = pkg.basePrices[vehicleType] || pkg.basePrices.small;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`glass rounded-2xl overflow-hidden ${pkg.popular ? "ring-2 ring-cyan-400" : ""}`}
                >
                  {pkg.popular && (
                    <div className="bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-900 text-center py-2 text-sm font-bold">
                      Most Popular
                    </div>
                  )}
                  
                  <div className="p-6 md:p-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">{pkg.name}</h2>
                        {pkg.includes && (
                          <p className="text-cyan-400 text-sm mb-2">{pkg.includes}</p>
                        )}
                        <p className="text-slate-400">{pkg.tagline}</p>
                      </div>
                      <div className="mt-4 md:mt-0 text-right">
                        <div className="text-4xl md:text-5xl font-black gradient-text">${price}</div>
                        <p className="text-slate-500 text-sm">{vehicleType === 'small' ? 'Small car' : vehicleType === 'medium' ? 'SUV/Medium' : vehicleType === 'large' ? 'Large/Van' : 'XL/Truck'}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-6">
                      <div>
                        <h3 className="font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          <Sparkles className="w-5 h-5" />
                          Interior Inclusions
                        </h3>
                        <ul className="space-y-2">
                          {pkg.interior.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h3 className="font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          <Droplets className="w-5 h-5" />
                          Exterior Inclusions
                        </h3>
                        <ul className="space-y-2">
                          {pkg.exterior.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-700">
                      <Button
                        onClick={() => handleBookPackage(key)}
                        className={`flex-1 rounded-full py-3 font-semibold ${
                          pkg.popular 
                            ? "btn-primary-glow text-slate-900" 
                            : "bg-slate-700 hover:bg-slate-600 text-white"
                        }`}
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book This Package
                      </Button>
                    </div>

                    <p className="text-xs text-slate-500 mt-4 text-center">
                      *Heavy duty cleaning may include additional charges
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Add-ons Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">
              Optional <span className="gradient-text">Add-ons</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {ADDONS.map((addon) => (
                <div key={addon.id} className="glass rounded-xl p-4 text-center">
                  <p className="font-medium text-slate-300 mb-2">{addon.name}</p>
                  <p className="text-cyan-400 font-bold text-xl">+${addon.price}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

// Service Areas Page (separate page)
const ServiceAreasPage = () => {
  const [suburb, setSuburb] = useState("");
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const { setShowBookingModal } = useBooking();
  const debounceRef = useRef(null);

  const FREE_RADIUS_KM = 15;
  const FEE_PER_KM = 0.40;

  // Popular suburbs for quick selection grid
  const suburbs = [
    { name: "Thomastown", distance: 0 },
    { name: "Bundoora", distance: 5 },
    { name: "Mill Park", distance: 4 },
    { name: "Lalor", distance: 3 },
    { name: "Epping", distance: 5 },
    { name: "Preston", distance: 8 },
    { name: "Reservoir", distance: 6 },
    { name: "South Morang", distance: 7 },
    { name: "Doncaster", distance: 12 },
    { name: "Sunshine", distance: 18 },
    { name: "Footscray", distance: 20 },
    { name: "Melbourne CBD", distance: 18 },
    { name: "Craigieburn", distance: 8 },
    { name: "Broadmeadows", distance: 6 },
    { name: "Coburg", distance: 10 },
    { name: "Brunswick", distance: 12 }
  ];


  const calculateTravelFee = (distance) => {
    if (distance <= FREE_RADIUS_KM) return 0;
    return (distance - FREE_RADIUS_KM) * FEE_PER_KM;
  };

  // Fetch Google Places Autocomplete suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setPredictions([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { query }
      });
      setPredictions(response.data.predictions || []);
    } catch (error) {
      console.error("Autocomplete error:", error);
      setPredictions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSuburb(value);
    setShowSuggestions(true);
    setCalculatedFee(null);
    
    // Debounce API calls
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const selectPrediction = async (prediction) => {
    setSuburb(prediction.description || prediction.main_text);
    setShowSuggestions(false);
    setPredictions([]);
    await calculateFeeFromAPI(prediction.description || prediction.main_text);
  };

  const calculateFeeFromAPI = async (address) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate-travel-fee`, { address });
      if (response.data.success) {
        setCalculatedFee({
          fee: response.data.fee,
          distance: response.data.distance_km,
          suburb: address,
          source: "google"
        });
      } else {
        setCalculatedFee({ 
          fee: calculateTravelFee(25), 
          distance: 25, 
          suburb: address, 
          unknown: true, 
          source: "estimate" 
        });
      }
    } catch (error) {
      setCalculatedFee({ 
        fee: calculateTravelFee(25), 
        distance: 25, 
        suburb: address, 
        unknown: true, 
        source: "estimate" 
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = async () => {
    if (!suburb.trim()) return;
    setShowSuggestions(false);
    setPredictions([]);
    await calculateFeeFromAPI(suburb);
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="service-areas-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Service <span className="gradient-text">Areas</span>
            </h1>
            <p className="text-slate-400 text-lg">
              Based in Thomastown, servicing all of Melbourne
            </p>
          </motion.div>

          {/* Distance Calculator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8 mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 text-center">Calculate Travel Fee</h2>
            
            <div className="relative max-w-md mx-auto">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Start typing your suburb..."
                    value={suburb}
                    onChange={handleInputChange}
                    onFocus={() => setShowSuggestions(true)}
                    className="bg-slate-800 border-slate-700 pl-10"
                    data-testid="suburb-input"
                  />
                  {showSuggestions && predictions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg overflow-hidden z-50 max-h-64 overflow-y-auto shadow-xl">
                      {predictions.map((prediction, i) => (
                        <button
                          key={i}
                          onClick={() => selectPrediction(prediction)}
                          className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center gap-3 border-b border-slate-700 last:border-0"
                        >
                          <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{prediction.main_text}</span>
                            {prediction.secondary_text && (
                              <span className="text-slate-400 ml-1">{prediction.secondary_text}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchLoading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg p-4 z-50">
                      <div className="flex items-center gap-2 text-slate-400">
                        <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Searching...</span>
                      </div>
                    </div>
                  )}
                </div>
                <Button 
                  onClick={calculateFee}
                  className="btn-primary-glow text-slate-900 px-6"
                >
                  Check
                </Button>
              </div>
              
              {loading && (
                <div className="mt-6 p-6 bg-slate-800 rounded-xl text-center">
                  <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">Calculating distance via Google Maps...</p>
                </div>
              )}
              
              {!loading && calculatedFee !== null && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-6 bg-slate-800 rounded-xl text-center"
                >
                  <p className="text-slate-400 text-sm mb-2">
                    {calculatedFee.suburb} • {calculatedFee.distance}km from Thomastown
                    {calculatedFee.unknown && " (estimated)"}
                  </p>
                  {calculatedFee.fee === 0 ? (
                    <p className="text-green-400 font-bold text-2xl">Free Travel!</p>
                  ) : (
                    <>
                      <p className="text-cyan-400 font-bold text-2xl">
                        Travel Fee: ${calculatedFee.fee.toFixed(2)}
                      </p>
                      <p className="text-slate-500 text-sm mt-2">
                        ({(calculatedFee.distance - FREE_RADIUS_KM).toFixed(1)}km beyond free zone × $0.40/km)
                      </p>
                    </>
                  )}
                  {calculatedFee.source === "google" && (
                    <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Powered by Google Maps
                    </p>
                  )}
                  <Button 
                    onClick={() => setShowBookingModal(true)}
                    className="btn-primary-glow text-slate-900 mt-4"
                  >
                    Book Now
                  </Button>
                </motion.div>
              )}
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-slate-500 text-sm">
                Free travel within {FREE_RADIUS_KM}km • ${FEE_PER_KM.toFixed(2)}/km beyond
              </p>
            </div>
          </motion.div>

          {/* Service Areas Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-center">Coverage Areas</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {suburbs.slice(0, 16).map((s, i) => {
                const fee = calculateTravelFee(s.distance);
                return (
                  <div 
                    key={i} 
                    className={`service-area-card cursor-pointer ${fee === 0 ? "free" : ""}`}
                    onClick={() => calculateFeeFromAPI(s.name)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{s.name}</span>
                      {fee === 0 ? (
                        <span className="text-xs text-green-400 font-medium">Free</span>
                      ) : (
                        <span className="text-xs text-slate-400">+${fee.toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-slate-500 text-sm mt-6">
              Don't see your area? Use the calculator above or contact us!
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const { setShowBookingModal } = useBooking();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitted(true);
        toast.success("Message sent! We'll get back to you soon.");
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="contact-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Contact <span className="gradient-text">Us</span>
            </h1>
            <p className="text-slate-400 text-lg">
              Get in touch with us for any questions or to book your detail
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">Get In Touch</h2>
              
              <div className="space-y-6">
                <a href="tel:0478054648" className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Call or Text</p>
                    <p className="font-medium">0478 054 648</p>
                  </div>
                </a>
                
                <a href="mailto:hello.rainxpress@gmail.com" className="flex items-center gap-4 text-slate-300 hover:text-cyan-400 transition">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium">hello.rainxpress@gmail.com</p>
                  </div>
                </a>
                
                <div className="flex items-center gap-4 text-slate-300">
                  <div className="w-12 h-12 rounded-full bg-cyan-400/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-medium">Thomastown, VIC</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Button 
                  onClick={() => setShowBookingModal(true)}
                  className="w-full btn-primary-glow text-slate-900 font-semibold py-3 rounded-full"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Now
                </Button>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-2xl p-8"
            >
              <h2 className="text-2xl font-bold mb-6">Send a Message</h2>
              
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">Name</Label>
                    <Input
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Email</Label>
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Phone</Label>
                    <Input
                      placeholder="0400 000 000"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Message</Label>
                    <Textarea
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="bg-slate-800 border-slate-700"
                      rows={4}
                      required
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-slate-700 hover:bg-slate-600 py-3 rounded-full">
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <p className="text-xl font-medium mb-2">Message Sent!</p>
                  <p className="text-slate-400">We'll get back to you as soon as possible.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

// Terms & Conditions Page
const TermsPage = () => {
  return (
    <div className="min-h-screen bg-slate-950" data-testid="terms-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Terms & <span className="gradient-text">Conditions</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-8 space-y-8"
          >
            {/* Key Requirements */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Key Requirements</h2>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide safe, legal parking space</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Allow sufficient space around car (approx. 1m)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide access to power (if required)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Provide access to water (if required)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Ensure vehicle is unlocked at arrival</span>
                </li>
              </ul>
            </div>

            {/* Before Service */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Before Service</h2>
              <ul className="space-y-2 text-slate-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Remove all personal belongings from interior & boot</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>Remove all valuables</span>
                </li>
              </ul>
              <p className="text-sm text-slate-500 mt-4">
                *We are not responsible for personal items left inside the vehicle
              </p>
            </div>

            {/* Cancellation Policy */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Cancellation Policy</h2>
              <ul className="space-y-2 text-slate-300">
                <li>• <span className="text-green-400">48+ hours:</span> Free reschedule</li>
                <li>• <span className="text-yellow-400">24-48 hours:</span> Deposit retained</li>
                <li>• <span className="text-red-400">Under 24 hours:</span> 50% service fee may apply</li>
              </ul>
            </div>

            {/* Condition Disclosure */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Condition Disclosure</h2>
              <p className="text-slate-300 mb-3">Customers must accurately declare:</p>
              <ul className="space-y-1 text-slate-400">
                <li>• Excessive pet hair, sand/mud buildup</li>
                <li>• Mold, biohazard, vomit, bodily fluids</li>
                <li>• Heavy stains, smoke odor</li>
              </ul>
              <p className="text-cyan-400 font-medium mt-3">
                Undisclosed heavy conditions may result in additional charges or rescheduling.
              </p>
              <p className="text-slate-400 mt-3">
                <span className="text-cyan-400 font-medium">Unsure about your car's condition?</span> You can upload photos during checkout or contact us via our <a href="/contact" className="text-cyan-400 underline hover:text-cyan-300">contact page</a> and we will get back to you with an accurate quote.
              </p>
            </div>

            {/* Payment Policy */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Payment Policy</h2>
              <ul className="space-y-2 text-slate-300">
                <li>• Payment due immediately after service</li>
                <li>• Card & cash accepted</li>
                <li>• No "pay later" options</li>
                <li>• <span className="text-cyan-400">A deposit may be requested to confirm your booking</span></li>
              </ul>
              <p className="mt-4 text-slate-300 bg-slate-800/50 p-3 rounded-lg">
                <span className="text-cyan-400 font-medium">Unsure about your booking?</span> Text, call or email us and we will respond same day.
              </p>
            </div>

            {/* Weather & Safety */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Weather & Safety</h2>
              <p className="text-slate-300 mb-3">
                Service may require rescheduling for safety and quality reasons due to weather.
              </p>
              <p className="text-cyan-400 mb-3">
                In case of rain or thunderstorm, we may be able to do service if there is shade that protects from rain.
              </p>
              <p className="text-slate-300 mb-2">We will refuse service if:</p>
              <ul className="space-y-1 text-slate-400">
                <li>• Unsafe location</li>
                <li>• Aggressive behaviour</li>
                <li>• Hazardous materials</li>
                <li>• Undeclared biohazard contamination</li>
              </ul>
            </div>

            {/* Satisfaction Guarantee */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Satisfaction Guarantee</h2>
              <p className="text-slate-300">
                If you are not fully satisfied, notify us on site and we will address concerns immediately.
              </p>
              <p className="text-slate-300 mt-3">
                We are not responsible for personal items left inside the vehicle.
              </p>
              <p className="text-slate-300 mt-2">
                We are not responsible for pre-existing damage, worn trim, loose panels or fragile components.
              </p>
            </div>

            {/* Photo & Media Consent */}
            <div>
              <h2 className="text-xl font-bold text-cyan-400 mb-4">Photo & Media Consent</h2>
              <p className="text-slate-300">
                We may take before and after photos for quality control and marketing.
              </p>
              <p className="text-cyan-400 mt-2">
                License plates and personal details will not be displayed without permission.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};


// Email Verification Page
const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No verification token provided");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus("success");
        setMessage(`Email verified: ${response.data.email}`);
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Verification failed");
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="verify-email-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-8 text-center"
          >
            {status === "verifying" && (
              <>
                <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Verifying Email</h1>
                <p className="text-slate-400">Please wait...</p>
              </>
            )}
            
            {status === "success" && (
              <>
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Email Verified!</h1>
                <p className="text-slate-400 mb-6">{message}</p>
                <Button 
                  onClick={() => navigate("/account")}
                  className="btn-primary-glow text-slate-900"
                >
                  Go to My Account
                </Button>
              </>
            )}
            
            {status === "error" && (
              <>
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Verification Failed</h1>
                <p className="text-slate-400 mb-6">{message}</p>
                <Button 
                  onClick={() => navigate("/account")}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  Go to My Account
                </Button>
              </>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Reset Password Page
const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, valid, success, error
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const token = searchParams.get("token");

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus("error");
        setMessage("No reset token provided");
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setEmail(response.data.email);
        setStatus("valid");
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Invalid or expired reset link");
      }
    };

    verifyToken();
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setStatus("success");
      toast.success("Password reset successful!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="reset-password-page">
      <Header />
      
      <section className="pt-32 pb-20 px-4 md:px-8">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl p-8"
          >
            {status === "loading" && (
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Verifying Link</h1>
                <p className="text-slate-400">Please wait...</p>
              </div>
            )}
            
            {status === "valid" && (
              <>
                <h1 className="text-2xl font-bold mb-2 text-center">Reset Password</h1>
                <p className="text-slate-400 text-center mb-6">
                  Enter a new password for <span className="text-cyan-400">{email}</span>
                </p>
                
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <Label className="text-sm mb-2 block">New Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-slate-800 border-slate-700"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block">Confirm Password</Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-slate-800 border-slate-700"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full btn-primary-glow text-slate-900 py-3 rounded-full"
                    data-testid="reset-password-btn"
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </Button>
                </form>
              </>
            )}
            
            {status === "success" && (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Password Reset!</h1>
                <p className="text-slate-400 mb-6">Your password has been reset successfully.</p>
                <Button 
                  onClick={() => navigate("/account")}
                  className="btn-primary-glow text-slate-900"
                >
                  Sign In
                </Button>
              </div>
            )}
            
            {status === "error" && (
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold mb-2">Invalid Link</h1>
                <p className="text-slate-400 mb-6">{message}</p>
                <Button 
                  onClick={() => navigate("/account")}
                  className="btn-primary-glow text-slate-900"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Main Home Component
const Home = () => {
  const { setShowDiscountPopup, setPopupType } = useBooking();
  const [hasShownScrollPopup, setHasShownScrollPopup] = useState(() => {
    // Check if popup was shown in the last 24 hours
    const lastShown = localStorage.getItem('rainxpress_popup_shown');
    if (lastShown) {
      const hoursSince = (Date.now() - parseInt(lastShown)) / (1000 * 60 * 60);
      return hoursSince < 24; // Don't show again for 24 hours
    }
    return false;
  });
  const [hasShownExitPopup, setHasShownExitPopup] = useState(() => {
    // Exit popup only once per session
    return sessionStorage.getItem('rainxpress_exit_popup_shown') === 'true';
  });

  // Show popup after scrolling (once per 24 hours)
  useEffect(() => {
    const handleScroll = () => {
      if (!hasShownScrollPopup && window.scrollY > window.innerHeight * 0.5) {
        setPopupType("normal_10");
        setShowDiscountPopup(true);
        setHasShownScrollPopup(true);
        localStorage.setItem('rainxpress_popup_shown', Date.now().toString());
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasShownScrollPopup, setShowDiscountPopup, setPopupType]);

  // Exit intent popup (once per session, only after scroll popup was dismissed)
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && hasShownScrollPopup && !hasShownExitPopup) {
        setPopupType("exit_15");
        setShowDiscountPopup(true);
        setHasShownExitPopup(true);
        sessionStorage.setItem('rainxpress_exit_popup_shown', 'true');
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [hasShownScrollPopup, hasShownExitPopup, setShowDiscountPopup, setPopupType]);

  return (
    <div data-testid="home-page">
      <Header />
      <HeroSection />
      <EmotionalSection />
      <PackagesSection />
      <WhyChooseUsSection />
      <ServiceAreasSection />
      <ReviewsSection />
      <PoliciesSection />
      <TradeSectionComingSoon />
      <AccountBenefitsSection />
      <FinalCTASection />
      <Footer />
      <BookingModal />
      <DiscountPopup />
    </div>
  );
};


function App() {
  return (
    <AuthProvider>
      <BookingProvider>
        <div className="App bg-slate-950 min-h-screen">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<PackagesPageNew />} />
              <Route path="/packages" element={<PackagesPageNew />} />
              <Route path="/service-areas" element={<ServiceAreasPageNew />} />
              <Route path="/contact" element={<ContactPageNew />} />
              <Route path="/terms" element={<TermsPageNew />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/booking-success" element={<BookingSuccessPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/trade" element={<TradePage />} />
              <Route path="/admin" element={<AdminPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
        </div>
      </BookingProvider>
    </AuthProvider>
  );
}

export default App;
